import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';

/**
 * Hook para gerenciar bloqueio por inatividade
 * 
 * @param {number} tempoInatividade - Tempo em minutos para bloqueio (padrão: 30)
 * @param {boolean} ativo - Se o sistema de inatividade está ativo
 */
export function useInatividade(tempoInatividade = 30, ativo = true) {
    const navigate = useNavigate();
    const location = useLocation();
    const timeoutRef = useRef(null);
    const ultimaAtividadeRef = useRef(Date.now());
    const throttleTimeoutRef = useRef(null);

    // Função para verificar se deve aplicar inatividade
    const verificarSeDeveAplicar = useCallback(() => {
        const paginasExcluidas = ['/login', '/bloqueio', '/consulta'];
        const temToken = !!localStorage.getItem('token');
        const naoEstaEmPaginaExcluida = !paginasExcluidas.includes(location.pathname);
        return ativo && temToken && naoEstaEmPaginaExcluida;
    }, [ativo, location.pathname]);

    const resetarTimer = useCallback(() => {
        const deveAplicar = verificarSeDeveAplicar();
        if (!deveAplicar) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        // Limpa timer anterior
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        // Atualiza última atividade
        ultimaAtividadeRef.current = Date.now();

        // Define novo timer
        const tempoMs = tempoInatividade * 60 * 1000; // Converte minutos para milissegundos
        
        console.log(`[INATIVIDADE] Timer reiniciado. Bloqueio em ${tempoInatividade} minutos (${tempoMs}ms)`);
        
        timeoutRef.current = setTimeout(() => {
            console.log('[INATIVIDADE] ⚠️ Tempo de inatividade atingido! Bloqueando sessão...');
            
            // Verifica se ainda está na mesma página e visível
            const tokenExiste = localStorage.getItem('token');
            const aindaDeveAplicar = verificarSeDeveAplicar();
            if (aindaDeveAplicar && document.visibilityState === 'visible' && tokenExiste) {
                console.log('[INATIVIDADE] Redirecionando para tela de bloqueio...');
                localStorage.removeItem('token');
                navigate('/bloqueio', { 
                    state: { 
                        motivo: 'inatividade',
                        limparToken: true 
                    },
                    replace: true
                });
            } else {
                console.log('[INATIVIDADE] Bloqueio cancelado - página não visível ou sem token');
            }
        }, tempoMs);
    }, [tempoInatividade, ativo, verificarSeDeveAplicar, navigate]);

    // Função com throttle para evitar muitas chamadas
    const registrarAtividade = useCallback(() => {
        if (!verificarSeDeveAplicar()) return;

        // Throttle: só reseta timer a cada 5 segundos no máximo (para não resetar constantemente)
        if (throttleTimeoutRef.current) {
            return; // Já tem um reset agendado
        }

        throttleTimeoutRef.current = setTimeout(() => {
            throttleTimeoutRef.current = null;
            resetarTimer();
        }, 5000); // Throttle de 5 segundos - evita resetar constantemente mas ainda detecta atividade
    }, [verificarSeDeveAplicar, resetarTimer]);

    useEffect(() => {
        const deveAplicar = verificarSeDeveAplicar();
        
        if (!deveAplicar) {
            // Se inatividade está desativada ou está em página excluída, limpa timer
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (throttleTimeoutRef.current) {
                clearTimeout(throttleTimeoutRef.current);
                throttleTimeoutRef.current = null;
            }
            return;
        }

        // Eventos que indicam atividade do usuário
        const eventos = [
            'mousedown',
            'mousemove',
            'keydown', // Mudado de keypress para keydown (mais confiável)
            'keypress',
            'scroll',
            'touchstart',
            'click',
            'wheel',
            'focus',
            'blur'
        ];

        // Handler para mudança de visibilidade da página
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Quando a página volta a ficar visível, reseta o timer
                resetarTimer();
            }
        };

        // Inicia o timer
        resetarTimer();

        // Adiciona listeners com throttle
        eventos.forEach(evento => {
            document.addEventListener(evento, registrarAtividade, { passive: true, capture: true });
        });

        // Listener para mudança de visibilidade
        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Logs para debug
        console.log(`[INATIVIDADE] Sistema ativado. Tempo: ${tempoInatividade} minutos`);
        console.log(`[INATIVIDADE] Página atual: ${location.pathname}`);
        console.log(`[INATIVIDADE] Deve aplicar: ${deveAplicar}`);
        console.log(`[INATIVIDADE] Ativo: ${ativo}`);

        // Cleanup
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (throttleTimeoutRef.current) {
                clearTimeout(throttleTimeoutRef.current);
                throttleTimeoutRef.current = null;
            }
            eventos.forEach(evento => {
                document.removeEventListener(evento, registrarAtividade, { capture: true });
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [ativo, verificarSeDeveAplicar, tempoInatividade, resetarTimer, registrarAtividade, location.pathname]);

    return { registrarAtividade, resetarTimer };
}
